import { useQuery } from "@apollo/client/react";
import RecordingJobTypeSelection from "@components/common/record/RecordingJobTypeSelection";
import { isUUID, isTemplateGroup } from "@components/dashboard/utils";
import { DSChip } from "@components/design-system/molecules";
import { getSelectedTeamMemberName } from "@components/teams/utils";
import GET_TEAM_USER_NOTE_COUNTS from "@graphql/queries/GetTeamUserNoteCounts";
import {
  useAuth,
  useWatchUserWsEvent,
  useWindowDimensions,
} from "@hooks";
import { endOfDay, startOfDay } from "date-fns";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

import DateRangeSelection from "./DateRangeSelection";
import TeamFilterDropdown from "../teams/TeamFilterDropdown";

const formatDateTime = (date) => {
  return date ? new Date(date).toISOString() : null;
};

export function FilterOption({
  label,
  children,
  isActive,
  setActiveFilter,
  containerName,
  width = "w-auto",
  isApplied,
  onClear,
  value,
  dataTestId,
  keepOpenOnBlur = false,
}) {
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const { width: windowWidth } = useWindowDimensions();
  const isNarrowScreen = windowWidth < 500;
  const closeTimerRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(closeTimerRef.current);
    setActiveFilter(label);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setActiveFilter((current) =>
        current === label ? null : current,
      );
    }, 100);
  };

  useEffect(() => {
    return () => clearTimeout(closeTimerRef.current);
  }, []);

  // When a portaled dropdown closes (keepOpenOnBlur: true → false),
  // clear activeFilter so the popover doesn't stay stuck open.
  useEffect(() => {
    if (!keepOpenOnBlur) {
      setActiveFilter((current) =>
        current === label ? null : current,
      );
    }
  }, [keepOpenOnBlur, label, setActiveFilter]);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DSChip
        size="sm"
        variant="filter"
        applied={isApplied}
        onClick={() => setActiveFilter(label)}
        onDismiss={() => {
          onClear();
          setActiveFilter(null);
        }}
        dataTestId={dataTestId}
      >
        {isApplied ? truncateText(value) : label}
      </DSChip>

      <div
        className={`absolute origin-top-left top-full left-0 z-40 pt-1 ${
          isActive || keepOpenOnBlur ? "block" : "hidden"
        } ${isNarrowScreen ? `scale-[75%]` : ""}`}
        id={containerName}
      >
        <div
          className={`flex flex-col items-start justify-start rounded-xl shadow-md bg-primary border border-primary p-2 ${width}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default function NoteFilters({
  searchQuery,
  setQueryOptions,
  currentTeam,
  selectedTeamMembers,
  setSelectedTeamMembers,
  selectedJobType,
  setSelectedJobType,
  refreshCurrentTeam,
  noteFilter = "",
  selectedRange,
  setSelectedRange,
}) {
  const { userUuid } = useAuth();
  const [activeFilter, setActiveFilter] = useState(null);
  const [keepTeamFilterOpen, setKeepTeamFilterOpen] = useState(false);
  const [keepTemplateFilterOpen, setKeepTemplateFilterOpen] =
    useState(false);
  const hasDateFilter =
    selectedRange.startDate && selectedRange.endDate;
  const hasTeamFilter =
    (userUuid !== selectedTeamMembers[0] ||
      selectedTeamMembers.length > 1) &&
    selectedTeamMembers.length > 0;
  const hasJobTypeFilter = selectedJobType !== null;

  const noteCountQueryOptions = {
    filter: noteFilter,
    teamUuid: currentTeam?.uuid,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    jobType: selectedJobType?.value,
    searchQuery: searchQuery,
  };
  const { data, refetch: refetchTeamUserNoteCounts } = useQuery(
    GET_TEAM_USER_NOTE_COUNTS,
    {
      variables: noteCountQueryOptions,
      skip: !currentTeam?.uuid,
      notifyOnNetworkStatusChange: true,
    },
  );

  const checkTeamAndRefetchNoteCounts = () => {
    if (currentTeam?.uuid) {
      refetchTeamUserNoteCounts();
    }
  };
  useWatchUserWsEvent({
    onMessage: checkTeamAndRefetchNoteCounts,
    eventName: "note_update",
  });

  const userNoteCounts = (
    data?.teamUserNoteCounts?.noteCounts || []
  ).reduce(
    (acc, { userUuid, noteCount }) => ({
      ...acc,
      [userUuid]: noteCount,
    }),
    {},
  );

  const clearDateFilter = () => {
    setSelectedRange({
      startDate: null,
      endDate: null,
      key: "selection",
    });
    setQueryOptions((prev) => ({
      ...prev,
      startDate: null,
      endDate: null,
    }));
  };

  const clearTeamFilter = () => {
    if (userUuid) {
      setSelectedTeamMembers([userUuid]);
      setQueryOptions((prev) => ({
        ...prev,
        userUuids: [userUuid],
      }));
    }
  };

  const clearJobTypeFilter = () => {
    setSelectedJobType(null);
    setQueryOptions((prev) => ({
      ...prev,
      jobType: null,
    }));
  };

  const handleJobTypeChange = (newJobType) => {
    setSelectedJobType(newJobType);
    setQueryOptions((prev) => ({
      ...prev,
      jobType: newJobType?.value,
    }));
  };

  const handleApplyDateRange = (startDate, endDate) => {
    const formattedStartDate = startDate
      ? formatDateTime(startOfDay(startDate))
      : null;
    const formattedEndDate = endDate
      ? formatDateTime(endOfDay(endDate))
      : null;
    setSelectedRange({
      key: "selection",
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
    setQueryOptions((prevOptions) => ({
      ...prevOptions,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    }));
  };

  const getDateFilterValue = () => {
    if (!selectedRange.startDate || !selectedRange.endDate) return "";

    // If start and end dates are the same, show only one date
    if (
      moment(selectedRange.startDate).format("DD/MM/YY") ===
      moment(selectedRange.endDate).format("DD/MM/YY")
    ) {
      return moment(selectedRange.startDate).format("DD/MM/YY");
    }

    // Otherwise show the range
    return `${moment(selectedRange.startDate).format(
      "DD/MM/YY",
    )} - ${moment(selectedRange.endDate).format("DD/MM/YY")}`;
  };

  const formatJobType = (jobType) => {
    if (!jobType?.value) {
      return "";
    }
    if (isUUID(jobType.value) || isTemplateGroup(jobType.value)) {
      return jobType.name;
    }

    // Split on underscores and hyphens
    return jobType.value
      .split(/[-_]/)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(" ");
  };

  return (
    <div
      id="filter-options"
      className="flex flex-row flex-wrap items-center gap-1 justify-start"
    >
      <FilterOption
        label="Date"
        isActive={activeFilter === "Date"}
        setActiveFilter={setActiveFilter}
        containerName="date-filter"
        width="w-0"
        isApplied={hasDateFilter}
        onClear={clearDateFilter}
        value={getDateFilterValue()}
        dataTestId="date-filter-dropdown"
      >
        <DateRangeSelection
          selectedRange={selectedRange}
          handleApplyDateRange={handleApplyDateRange}
          isActive={activeFilter === "Date"}
          containerName="date-filter"
        />
      </FilterOption>

      {currentTeam?.uuid && (
        <FilterOption
          label="My Notes"
          isActive={activeFilter === "My Notes"}
          setActiveFilter={setActiveFilter}
          isApplied={hasTeamFilter}
          onClear={clearTeamFilter}
          value={getSelectedTeamMemberName(
            selectedTeamMembers,
            currentTeam,
            userUuid,
          )}
          dataTestId="team-filter-dropdown"
          keepOpenOnBlur={keepTeamFilterOpen}
        >
          <TeamFilterDropdown
            isActive={activeFilter === "My Notes"}
            userNoteCounts={userNoteCounts}
            currentTeam={currentTeam}
            selectedTeamMembers={selectedTeamMembers}
            setSelectedTeamMembers={setSelectedTeamMembers}
            refreshCurrentTeam={refreshCurrentTeam}
            onDropdownOpenChange={setKeepTeamFilterOpen}
          />
        </FilterOption>
      )}
      <FilterOption
        dataTestId="filter-template-dropdown"
        label="Template"
        isActive={activeFilter === "Template"}
        setActiveFilter={setActiveFilter}
        containerName="type-filter"
        isApplied={hasJobTypeFilter}
        onClear={clearJobTypeFilter}
        value={formatJobType(selectedJobType)}
        keepOpenOnBlur={keepTemplateFilterOpen}
      >
        <RecordingJobTypeSelection
          selectedJobType={selectedJobType}
          setSelectedJobType={handleJobTypeChange}
          defaultJobType={{ value: "", name: "All" }}
          onDropdownOpenChange={setKeepTemplateFilterOpen}
          hideTemplateGroups
        />
      </FilterOption>
    </div>
  );
}
